import { DatabasesQueryByIdsNodes } from '@tableau/data-catalog-client-components';
import { call, select } from 'redux-saga/effects';
import _ from 'underscore';

import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { deepClone } from '~commonInfra/utils/DeepClone';
import { Filter } from '~commonInfra/utils/Filter';
import { databasesByIds } from '~src/catalog/resources/DatabasesByIds';
import { IDataRoleInfo } from '~src/dataRole/models/IDataRoleInfo';
import { DataRoles } from '~src/dataRole/resources/Dataroles';
import { IDataConnectionInfo } from '~src/datasource/dataConnection/models/IDataConnectionInfo';
import { DataConnections } from '~src/datasource/dataConnection/resources/DataConnections';
import { IDataSourceDetailInfo, IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { DatasourceIdToConnectionIdsMap } from '~src/datasource/ServerApi/types/DatasourceIdToConnectionIdsMap';
import { selectItemFromHomeState } from '~src/home/selectors/HomeSelectors';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { MaxRequestArrayLength } from '~src/httpClient/utils/RequestHelper';
import { ILensInfo } from '~src/lens/models/ILensInfo';
import { Lenses } from '~src/lens/resources/Lenses';
import { IPredictiveModelInfo } from '~src/predictiveModel/models/IPredictiveModelInfo';
import { PredictiveModels } from '~src/predictiveModel/resources/PredictiveModels';
import { IProjectInfo } from '~src/projects/models/IProjectInfo';
import { Projects } from '~src/projects/resources/Projects';
import { IPublishedConnectionInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { ISimulationInfo } from '~src/simulation/models/ISimulationInfo';
import { Simulations } from '~src/simulation/resources/Simulations';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { isInCurrentSpace, selectContentFromAppState } from '~src/spacesPlatform/selectors/ContentSelectors';
import { selectSpaceInfo, selectSpaceInfoItem } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { makeItemsSet } from '~src/spacesPlatform/utils/ItemsSet';
import { IViewInfo } from '~src/views/models/IViewInfo';
import { Views } from '~src/views/resources/Views';
import { IWorkbookInfo } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';

import { IContentInfo } from '../models/IContentInfo';
import { isHomeSpace } from '../state/SpaceInfo';

export function isValidResult(result: IResult): boolean {
  return !result || !result.errors || result.errors.length === 0;
}

export function filterOutErroredItems(errors: IError[], items: IContentInfo[]): IContentInfo[] {
  if (!errors) {
    return items;
  }

  // Create set or errors with item id as the key.
  // Then filter out those items from given list of items
  let errorIds = makeItemsSet((error: IError) => error.id , ...errors);
  return _.filter(items, (item: IContentInfo) => !errorIds.hasOwnProperty(item.id));
}

export function getWorkbookInfos(workbooks: IContentInfo[]): Promise<IWorkbookInfo[]> {
  const idArray = workbooks.map(c => c.id);
  return getContentInfos(idArray, Workbooks);
}

export function getProjectInfos(projects: IContentInfo[]): Promise<IProjectInfo[]> {
  const idArray = projects.map(c => c.id);

  return getContentInfos(idArray, Projects);
}

export function getViewInfos(views: IContentInfo[]): Promise<IViewInfo[]> {
  const idArray = views.map(c => c.id);
  return getContentInfos(idArray, Views);
}

export function getDataRoleInfos(dataRoles: IContentInfo[]): Promise<IDataRoleInfo[]> {
  const idArray = dataRoles.map(c => c.id);
  return getContentInfos(idArray, DataRoles);
}

export function getLensInfos(lens: IContentInfo[]): Promise<ILensInfo[]> {
  const idArray = lens.map(c => c.id);
  return getContentInfos(idArray, Lenses);
}

export function getPredictiveModelInfos(predictiveModels: IContentInfo[]): Promise<IDataRoleInfo[]> {
  const idArray: string[] = predictiveModels.map(c => c.id);
  return getContentInfos(idArray, PredictiveModels);
}

export function getPublishedConnectionInfos(publishedConnections: IContentInfo[]): Promise<IDataRoleInfo[]> {
  const idArray: string[] = publishedConnections.map(c => c.id);
  return getContentInfos(idArray, PublishedConnections);
}

export function getSimulationInfos(simulations: IContentInfo[]): Promise<ISimulationInfo[]> {
  const idArray: string[] = simulations.map(c => c.id);
  return getContentInfos(idArray, Simulations);
}

export function getPublishedConnectionTableInfos(publishedConnectionTables: IContentInfo[]): Promise<IDataRoleInfo[]> {
  const idArray: string[] = publishedConnectionTables.map(c => c.id);
  return getContentInfos(idArray, PublishedConnectionTables);
}

export function getPublishedConnectionColumnInfos(publishedConnectionColumns: IContentInfo[]): Promise<IDataRoleInfo[]> {
  const idArray: string[] = publishedConnectionColumns.map(c => c.id);
  return getContentInfos(idArray, publishedConnectionColumns);
}

export function getDatabases(databases: IContentInfo[]): Promise<DatabasesQueryByIdsNodes[]> {
  const idArray = databases.map(c => c.contentDetail ? c.contentDetail.graphQLId : c.id);
  return getContentInfos(idArray, databasesByIds);
}

function getContentInfos<TItem extends Array<IViewInfo | IProjectInfo | IWorkbookInfo | IDataRoleInfo | ILensInfo | IPublishedConnectionInfo | IPredictiveModelInfo | DatabasesQueryByIdsNodes | ISimulationInfo>>(idArray: string[], contentResource: any): Promise<TItem>{
  if(idArray.length <= MaxRequestArrayLength) {
    return getContentInfoSlice(idArray, idArray.length, contentResource);
  }

  let idx: number = 0;
  const numSlices: number = Math.ceil(idArray.length / MaxRequestArrayLength);
  let outstandingPromises: Promise<TItem>[] = [];

  for(let i = 0; i < numSlices; i++) {
    outstandingPromises.push(getContentInfoSlice(idArray.slice(idx, idx + MaxRequestArrayLength), MaxRequestArrayLength, contentResource));
    idx = idx + MaxRequestArrayLength;
  }

  return Promise.all(outstandingPromises).then((resultArray: TItem[]) => {
    return _.flatten(resultArray as any) as TItem;
  });
}

function getContentInfoSlice<TItem>(idArray: string[], numElements: number, contentResource: any): Promise<TItem>{
  return contentResource.all()
                      .addFilter(Filter.stringFieldInArray('id', idArray))
                      .getSlice(0, numElements)
                      .then((list: any) => {
                        return list.items;
                      });
}

export function getDatasourceInfos(datasources: IContentInfo[]): Promise<IDataSourceInfo[]> {
  return DataSources.fromContent(...datasources);
}

// find the matching entity item from the selected content
export function* findSelectedContent(entityId: string, entityType: ExploreContentApi_ContentType){
  const spaceInfo = yield select(selectSpaceInfo);
  if(isHomeSpace(spaceInfo)) {
    return yield select(selectItemFromHomeState, entityId, entityType);
  } else {
    const items = yield select(selectContentFromAppState);
    const item = _.find(items, (item: IContentInfo) => entityId === item.id);
    return item;
  }
}

// TODO: This is a hack and exists only because getDatasourceActions only supports data connection IDs
export function* getCorrectEntityIdForActions(entityType: ExploreContentApi_ContentType, entityId: string) {
  if (entityType === 'dataconnection') {
    const item = yield call(findSelectedContent, entityId, entityType);
    // we need the datasource id to grab the actions for a dataconnection
    return (item as IDataConnectionInfo).datasource.id;
  }
  else if (entityType === 'datasource') {
    const inCurrentSpace = yield select(isInCurrentSpace, { id: entityId, spaceType: entityType });
    const item = inCurrentSpace ?
      yield select(selectSpaceInfoItem) : yield call(findSelectedContent, entityId, entityType);

    if (DataSources.isUnifiedDatasource(item)) {
      return item.id;
    }
    const id = yield call(getDataConnectionId, entityId);
    return id || entityId;
  }

  return entityId;
}

export function* getDataConnectionId(datasourceId: string) {
  return yield getDataConnectionIds([ datasourceId ])
    .then((map: DatasourceIdToConnectionIdsMap) => map[datasourceId] ? map[datasourceId][0] : null);
}

export function getDataConnectionIds(datasourceIds: string[]): Promise<DatasourceIdToConnectionIdsMap> {
  if(datasourceIds.length > 0) {
    return DataConnections.getConnectionIdsFromDatasourceIds(datasourceIds);
  } else {
    return Promise.resolve({} as DatasourceIdToConnectionIdsMap);
  }
}

export function replaceDatasourceIdWithConnectionIdForMixedContent(contentArray: ReadonlyArray<Readonly<IContentInfo>>): Promise<IContentInfo[]> {
  const unifiedDatasources: IContentInfo[] = [];
  const contentInfoDatasources: IContentInfo[] = [];
  contentArray.forEach(function(contentElement) {
    if (contentElement.contentType === 'datasource' && !DataSources.isUnifiedDatasource(contentElement)) {
      contentInfoDatasources.push(deepClone(contentElement));
    } else {
      unifiedDatasources.push(deepClone(contentElement));
    }
  });

  return getDataConnectionIds(contentInfoDatasources.map(i => i.id))
    .then((datasourceIdToDataConnectionIdsMap: DatasourceIdToConnectionIdsMap) => {
      const contentInfoDatasourcesWithUnifiedDatasourceIds =
        contentInfoDatasources.map(contentElement => {
          const dataConnectionIds = datasourceIdToDataConnectionIdsMap[ contentElement.id ];
          return { ...contentElement, id: dataConnectionIds[ 0 ] };
        });
      return contentInfoDatasourcesWithUnifiedDatasourceIds.concat(unifiedDatasources);
    });
}

/**
 * Note: Safari and Firefox will block this as a popup by default.
 * Consider checking the Window object returned is null and using a toast to
 * prompt the user to allow popups for the site if so.
 */
export function openBlankWindow(): Window {
  return WindowLocationService.open('');
}

export function setWindowLocation(window: Window, location: string): void {
  window.location.href = location;
}

export function getDatasourceDetailInfo(datasourceId: string): Promise<IDataSourceDetailInfo> {
  return DataSources.getById(datasourceId);
}
